import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePortalAuth } from '../../contexts/PortalAuthContext';
import { portalOrdersAPI, portalFeedsAPI, portalShippingPreferencesAPI, portalDeliveryScheduleAPI, portalShippingEstimateAPI } from '../../services/portalAPI';
import { salesOrdersAPI } from '../../services/api';
import PortalFooter from '../../components/Layout/PortalFooter';
import QuickAddProduct from '../../components/QuickAddProduct';
import TerminalSelector from '../../components/TerminalSelector';
// DeliveryWarningBanner removed - inline delivery helper shows correct dates with lead time
import { TutorialAutoStart, TutorialHelpButton } from '../../components/Portal/Tutorial';
import '../../styles/portal-brand.css';

interface FeedFormula {
  id: number;
  name: string;
  description: string;
  feed_grade: string;
  animal_type: string;
  bag_size: number;
  price_bag_mash: number;
  price_bag_pellet: number;
  price_byoc_mash: number;
  price_byoc_pellet: number;
  price_1000lb_mash: number;
  price_1000lb_pellet: number;
  price_2000lb_mash: number;
  price_2000lb_pellet: number;
  pellets_available: boolean;
  default_form: string;
  available_forms: string[];
}

interface OrderLineItem {
  id: string;
  formula: FeedFormula | null;
  selectedGrade: string;
  selectedAnimal: string;
  isPellet: boolean;
  // 'bags' = the formula's own bag (40 lb Five Star, 50 lb Essentials, 19 lb Tiny Dino),
  // priced per bag. 'bulk' = super sacks / totes, priced per pound. The form never lets
  // the two disagree with the numbers: switching re-derives count and weight.
  packaging: Packaging;
  totalPounds: number;
  numberOfPackages: number; // bags: how many bags. bulk: how many super sacks.
  packageSizeLbs: number;   // bags: the bag weight. bulk: pounds per sack.
  calculatedPrice?: {
    item_total: number;
    price_per_lb?: number;
    price_per_package?: number;
    pricing_display_method?: string;
    unit_price?: number;
  };
}

const PORTAL_SHIPPING_METHODS = [
  'Will Call - Kettle Falls',
  'Terminal Pickup',
  'Ship to Store',
  'Ship to Farm',
  'Milk Run',
];

interface DeliverySchedule {
  delivery_day: string;
  delivery_days_display?: string;
  next_delivery_date: string;
  next_delivery_formatted?: string;
  ship_by_date: string;
  ship_by_formatted?: string;
  order_cutoff_date: string;
  order_cutoff_formatted?: string;
  ship_day: string;
  can_make_nearest: boolean;
  days_until_delivery: number;
  is_daily?: boolean;
  remote_fee?: number | null;
}

// Bags or bulk. That is the only packaging question a customer answers; the bag SIZE
// comes from the feed (FeedFormula.bag_size - 40 / 50 / 19), never from a picker.
//
// Order 2610-0006 (2026-10-12): the old three-way picker ("Bagged 40 / Bagged 19 /
// Bulk (Custom)") kept the bag count when the customer switched to Bulk, so 65 x 40 lb
// went to the mill labelled bulk - priced by the pound and packed as 65 "bulk" units.
type Packaging = 'bags' | 'bulk';

const SUPER_SACK_MAX_LBS = 2600;
const BULK_MINIMUM_LBS = 1000;

const bagSizeOf = (formula: FeedFormula | null): number =>
  formula && Number(formula.bag_size) > 0 ? Number(formula.bag_size) : 40;

const bagWord = (lbs: number) => (lbs === 19 ? 'Tiny Dino 19 lb bag' : `${lbs} lb bag`);

// What the API calls this line. The server re-derives it from the numbers anyway
// (order_service.reconcile_package_type); this just says the same thing up front.
const apiPackageType = (item: OrderLineItem): 'BAGS' | 'BAGS_19' | 'BULK' =>
  item.packaging === 'bulk' ? 'BULK' : bagSizeOf(item.formula) === 19 ? 'BAGS_19' : 'BAGS';

const previewPackageType = (item: OrderLineItem): 'bags' | 'bags_19' | 'bulk' =>
  item.packaging === 'bulk' ? 'bulk' : bagSizeOf(item.formula) === 19 ? 'bags_19' : 'bags';

// Bags: whole bags of the formula's size - a weight rounds UP to the next bag.
const asBags = (item: OrderLineItem, pounds: number, bags?: number): OrderLineItem => {
  const size = bagSizeOf(item.formula);
  const count = bags !== undefined ? Math.max(0, Math.floor(bags))
    : pounds > 0 ? Math.ceil(pounds / size) : 0;
  return { ...item, packaging: 'bags', packageSizeLbs: size, numberOfPackages: count, totalPounds: count * size };
};

// Bulk: the weight as typed, split across N super sacks.
const asBulk = (item: OrderLineItem, pounds: number, sacks: number): OrderLineItem => {
  const count = Math.max(1, Math.floor(sacks) || 1);
  return { ...item, packaging: 'bulk', numberOfPackages: count, totalPounds: pounds, packageSizeLbs: pounds > 0 ? Math.round(pounds / count) : 0 };
};

const PortalPlaceOrder: React.FC = () => {
  const { user } = usePortalAuth();
  const navigate = useNavigate();

  // Order details
  const [deliveryDate, setDeliveryDate] = useState('');
  const [shippingMethod, setShippingMethod] = useState(PORTAL_SHIPPING_METHODS[0]);
  // null = customer has not yet made the required explicit liftgate choice.
  // Never defaults to false — for Ship to Store the customer must pick before submit.
  const [liftgateRequired, setLiftgateRequired] = useState<boolean | null>(null);
  const [pickupTerminal, setPickupTerminal] = useState<number | null>(null);
  const [isRush, setIsRush] = useState(false);
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [customerPONumber, setCustomerPONumber] = useState('');

  // Line items
  const [lineItems, setLineItems] = useState<OrderLineItem[]>([
    {
      id: crypto.randomUUID(),
      formula: null,
      selectedGrade: '',
      selectedAnimal: '',
      isPellet: false,
      packaging: 'bags',
      totalPounds: 0,
      numberOfPackages: 0,
      packageSizeLbs: 40,
    },
  ]);

  const [feedGrades, setFeedGrades] = useState<Array<{ value: string; label: string }>>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Delivery schedule for smart date picker
  const [deliverySchedule, setDeliverySchedule] = useState<DeliverySchedule | null>(null);
  const [deliveryDateWarning, setDeliveryDateWarning] = useState<string>('');
  const [validDeliveryDates, setValidDeliveryDates] = useState<string[]>([]);
  // The multi-pallet bag discount the quote applied: {pallets, per_bag, bags, savings}.
  // The line prices and subtotal already include it; this is the line that says so.
  const [palletDiscount, setPalletDiscount] = useState<{ pallets: number; per_bag: number; bags: number; savings: number } | null>(null);

  // Shipping estimate state
  interface ShippingEstimate {
    total: number;
    pallets: number;
    base_cost: number;
    farm_fee: number;
    liftgate_fee: number;
    retail_discount: number;
    breakdown_text: string;
    total_weight_lbs: number;
    bags_40lb_count: number;
    bags_to_free_shipping: number;
    incentive_message: string | null;
    is_retail_partner: boolean;
    is_will_call: boolean;
  }
  const [shippingEstimate, setShippingEstimate] = useState<ShippingEstimate | null>(null);
  const [loadingShipping, setLoadingShipping] = useState(false);
  const shippingDebounceRef = useRef<NodeJS.Timeout | null>(null);

  // Ref to track last pricing request to avoid duplicate API calls
  const lastPriceRequestRef = useRef<string>('');

  // Format date for display (e.g., "Jan 15")
  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T12:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  useEffect(() => {
    loadFeedGrades();
    loadShippingPreferences();
    loadReorderData();
  }, []);

  // Methods that need freight delivery scheduling
  const isFreightMethod = (method: string) => {
    const m = method.toLowerCase();
    return m.includes('ltl') || m === 'terminal pickup' || m === 'ship to store' || m === 'ship to farm';
  };

  // Fetch delivery schedule when shipping method changes (for freight methods)
  useEffect(() => {
    if (isFreightMethod(shippingMethod)) {
      loadDeliverySchedule();
    } else {
      setDeliverySchedule(null);
    }
  }, [shippingMethod]);

  // Validate delivery date against customer's delivery day(s)
  useEffect(() => {
    if (!deliveryDate || !deliverySchedule) {
      setDeliveryDateWarning('');
      return;
    }

    const selected = new Date(deliveryDate + 'T12:00:00'); // avoid timezone issues
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const selectedDay = dayNames[selected.getDay()];
    const selectedDayNum = selected.getDay();

    // For daily delivery zones (Tue-Fri), check if selected day is in range
    if (deliverySchedule.is_daily) {
      const validDays = [2, 3, 4, 5]; // Tuesday=2, Wednesday=3, Thursday=4, Friday=5
      if (!validDays.includes(selectedDayNum)) {
        setDeliveryDateWarning(
          `${selectedDay} is not a delivery day. Daily delivery is available Tuesday–Friday.`
        );
      } else {
        setDeliveryDateWarning('');
      }
    } else if (deliverySchedule.delivery_day) {
      // Single delivery day - check exact match
      if (selectedDay !== deliverySchedule.delivery_day) {
        setDeliveryDateWarning(
          `${selectedDay} is not your delivery day. Peninsula delivers on ${deliverySchedule.delivery_day}s.`
        );
      } else {
        setDeliveryDateWarning('');
      }
    } else {
      setDeliveryDateWarning('');
    }
  }, [deliveryDate, deliverySchedule]);

  // Fetch shipping estimate when line items or shipping method changes
  useEffect(() => {
    // Clear any pending debounce
    if (shippingDebounceRef.current) {
      clearTimeout(shippingDebounceRef.current);
    }

    // Check if we have any valid items
    const validItems = lineItems.filter(item => item.formula && item.totalPounds > 0);
    if (validItems.length === 0) {
      setShippingEstimate(null);
      return;
    }

    // Debounce the API call by 500ms
    shippingDebounceRef.current = setTimeout(async () => {
      setLoadingShipping(true);
      try {
        const itemsPayload = validItems.map(item => ({
          formula_id: item.formula?.id as number,
          quantity_lbs: item.totalPounds,
          num_packages: item.numberOfPackages,
          package_type: apiPackageType(item),
        }));

        const response = await portalShippingEstimateAPI.estimate({
          items: itemsPayload,
          shipping_method: shippingMethod.toUpperCase().replace(/\s+/g, '_').replace(/-/g, '_'),
          liftgate: liftgateRequired === true,
        });

        setShippingEstimate(response.data);
      } catch (err) {
        console.error('Error fetching shipping estimate:', err);
      } finally {
        setLoadingShipping(false);
      }
    }, 500);

    // Cleanup on unmount
    return () => {
      if (shippingDebounceRef.current) {
        clearTimeout(shippingDebounceRef.current);
      }
    };
  }, [lineItems, shippingMethod, liftgateRequired]);

  // Will Call closed day validation
  const getWillCallWarning = () => {
    if (!shippingMethod.toLowerCase().includes('will call') || !deliveryDate) {
      return '';
    }
    const selected = new Date(deliveryDate + 'T12:00:00');
    const dayOfWeek = selected.getDay();
    // Sunday = 0, Monday = 1, Saturday = 6
    // Will Call pickup is only Tuesday-Friday, 2:00-4:45 PM
    if (dayOfWeek === 0 || dayOfWeek === 1 || dayOfWeek === 6) {
      return 'Will Call pickup is Tuesday-Friday, 2:00-4:45 PM only.';
    }
    return '';
  };

  const willCallWarning = getWillCallWarning();

  const loadDeliverySchedule = async () => {
    try {
      const response = await portalDeliveryScheduleAPI.get();
      const schedule = response.data;
      setDeliverySchedule(schedule);

      // Auto-fill delivery date if not already set and we have a schedule
      if (schedule.next_delivery_date && !deliveryDate) {
        setDeliveryDate(schedule.next_delivery_date);
      }
    } catch (err) {
      console.log('No delivery schedule available');
      setDeliverySchedule(null);
    }
    // Also fetch valid dates for the date picker
    try {
      const datesResp = await portalDeliveryScheduleAPI.getValidDates();
      if (datesResp.data?.valid_dates) {
        setValidDeliveryDates(datesResp.data.valid_dates.map((d: any) => d.date));
      }
    } catch {
      setValidDeliveryDates([]);
    }
  };

  // Load customer's preferred shipping method
  const loadShippingPreferences = async () => {
    try {
      const response = await portalShippingPreferencesAPI.get();
      const prefs = response.data;

      // Map the backend shipping method value to the display name
      const methodMap: Record<string, string> = {
        'will_call': 'Will Call - Kettle Falls',
        'ltl_terminal': 'Terminal Pickup',
        'ltl_store': 'Ship to Store',
        'ltl_farm': 'Ship to Farm',
        'milk_run': 'Milk Run',
        // Legacy mappings
        'ltl_dock': 'Ship to Store',
        'ltl_business': 'Ship to Store',
      };

      if (prefs.preferred_shipping_method && methodMap[prefs.preferred_shipping_method]) {
        setShippingMethod(methodMap[prefs.preferred_shipping_method]);
      }

      // Also set liftgate if they require it
      if (prefs.requires_liftgate) {
        setLiftgateRequired(true);
      }
    } catch (err) {
      console.log('No shipping preferences found, using defaults');
    }
  };

  // Pricing recalculation
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      calculatePricing();
    }, 500);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItems]);

  const loadFeedGrades = async () => {
    try {
      // Use portal API to get only feeds available to this customer
      const response = await portalFeedsAPI.getFilterOptions({});
      const grades = response.data.grades || [];

      // Sort: Essential → Plus → Five Star
      const gradeOrder = ['ESSENTIAL', 'PLUS', 'FIVE_STAR'];
      const sortedGrades = grades.sort((a: any, b: any) => {
        const aIndex = gradeOrder.indexOf(a.value);
        const bIndex = gradeOrder.indexOf(b.value);
        return aIndex - bIndex;
      });

      setFeedGrades(sortedGrades);
    } catch (err) {
      console.error('Error loading feed grades:', err);
    }
  };

  const loadAnimalTypes = async (grade: string) => {
    try {
      // Use portal API to get only feeds available to this customer
      const response = await portalFeedsAPI.getFilterOptions({ feed_grade: grade });
      return response.data.animal_types || [];
    } catch (err) {
      console.error('Error loading animal types:', err);
      return [];
    }
  };

  const loadFormulas = async (grade: string, animal: string) => {
    try {
      // Use portal API to get only feeds available to this customer
      const response = await portalFeedsAPI.getFilterOptions({
        feed_grade: grade,
        animal_type: animal,
      });
      return response.data.formulas || [];
    } catch (err) {
      console.error('Error loading formulas:', err);
      return [];
    }
  };

  const loadReorderData = () => {
    const reorderDataStr = localStorage.getItem('reorder_data');
    if (reorderDataStr) {
      try {
        const reorderData = JSON.parse(reorderDataStr);
        if (reorderData.shipping_method) {
          setShippingMethod(reorderData.shipping_method);
        }
        if (reorderData.is_rush !== undefined) {
          setIsRush(reorderData.is_rush);
        }
        if (reorderData.special_instructions) {
          setSpecialInstructions(reorderData.special_instructions);
        }
        localStorage.removeItem('reorder_data');
        setTimeout(() => {
          alert('Order items loaded! Please review and update the delivery date.');
        }, 500);
      } catch (err) {
        console.error('Failed to load reorder data:', err);
      }
    }
  };

  const handleGradeChange = (lineItemId: string, grade: string) => {
    setLineItems((prev) =>
      prev.map((item) =>
        item.id === lineItemId
          ? {
              ...item,
              selectedGrade: grade,
              selectedAnimal: '',
              formula: null,
              isPellet: false,
            }
          : item
      )
    );
  };

  const handleAnimalChange = (lineItemId: string, animal: string) => {
    setLineItems((prev) =>
      prev.map((item) =>
        item.id === lineItemId
          ? { ...item, selectedAnimal: animal, formula: null, isPellet: false }
          : item
      )
    );
  };

  const handleFormulaChange = (lineItemId: string, formula: FeedFormula | null) => {
    let defaultIsPellet = false;
    if (formula && formula.default_form) {
      defaultIsPellet = formula.default_form === 'PELLET';
    }

    setLineItems((prev) =>
      prev.map((item) => {
        if (item.id !== lineItemId) return item;
        const next = { ...item, formula, isPellet: defaultIsPellet };
        // A different feed can mean a different bag (50 lb Essentials vs 40 lb Five
        // Star): keep the bag COUNT and let the weight follow the new bag size.
        return next.packaging === 'bags' ? asBags(next, 0, next.numberOfPackages) : next;
      })
    );

  };

  const handlePelletToggle = (lineItemId: string, isPellet: boolean) => {
    setLineItems((prev) =>
      prev.map((item) => (item.id === lineItemId ? { ...item, isPellet } : item))
    );
  };

  // Bags <-> bulk. The numbers are re-derived on every switch so a stale count can
  // never ride along under the other label (the 2610-0006 failure).
  const handlePackagingChange = (lineItemId: string, packaging: Packaging) => {
    setLineItems((prev) =>
      prev.map((item) => {
        if (item.id !== lineItemId || item.packaging === packaging) return item;
        return packaging === 'bags'
          ? asBags(item, item.totalPounds)        // 2,600 lb of a 40 lb bag feed -> 65 bags
          : asBulk(item, item.totalPounds, 1);    // the same weight, one super sack
      })
    );
  };

  // Just update the value - NO calculations during typing
  const handleTotalPoundsChange = (lineItemId: string, totalPounds: number) => {
    setLineItems((prev) =>
      prev.map((item) =>
        item.id === lineItemId ? { ...item, totalPounds } : item
      )
    );
  };

  // When the weight is done being typed: bags round up to whole bags; bulk keeps the
  // weight and re-splits it across the sacks already chosen.
  const handleTotalPoundsBlur = (lineItemId: string) => {
    setLineItems((prev) =>
      prev.map((item) => {
        if (item.id !== lineItemId) return item;
        return item.packaging === 'bulk'
          ? asBulk(item, item.totalPounds, item.numberOfPackages)
          : asBags(item, item.totalPounds);
      })
    );
  };

  // Just update the value - NO calculations during typing
  const handleNumberOfPackagesChange = (lineItemId: string, numberOfPackages: number) => {
    setLineItems((prev) =>
      prev.map((item) =>
        item.id === lineItemId ? { ...item, numberOfPackages } : item
      )
    );
  };

  // When the count is done being typed: bags -> weight follows the count; bulk -> the
  // weight stays and the per-sack figure follows the sack count.
  const handleNumberOfPackagesBlur = (lineItemId: string) => {
    setLineItems((prev) =>
      prev.map((item) => {
        if (item.id !== lineItemId) return item;
        return item.packaging === 'bulk'
          ? asBulk(item, item.totalPounds, item.numberOfPackages)
          : asBags(item, 0, item.numberOfPackages);
      })
    );
  };

  const calculatePricing = useCallback(async () => {
    if (!user?.customer) return;

    const validItems = lineItems.filter((item) => item.formula && item.totalPounds > 0);
    if (validItems.length === 0) {
      setLineItems((prev) =>
        prev.map((item) => ({
          ...item,
          calculatedPrice: undefined,
        }))
      );
      lastPriceRequestRef.current = '';
      return;
    }

    // Create request key to avoid duplicate API calls
    const requestKey = validItems.map((item) =>
      `${item.formula!.id}-${item.isPellet}-${item.totalPounds}-${item.numberOfPackages}-${item.packaging}`
    ).join('|');

    if (requestKey === lastPriceRequestRef.current) {
      return; // Skip - same request as last time
    }
    lastPriceRequestRef.current = requestKey;

    try {
      const payload = {
        customer_id: user.customer.id,
        items: validItems.map((item) => ({
          formula_id: item.formula!.id,
          is_pellet: item.isPellet,
          total_quantity_lbs: item.totalPounds,
          number_of_packages: item.numberOfPackages,
          package_type: previewPackageType(item),
        })),
      };

      const response = await salesOrdersAPI.calculatePrice(payload);
      const pricedItems = response.data.items;
      setPalletDiscount(response.data.pallet_discount || null);

      setLineItems((prev) =>
        prev.map((item) => {
          const pricedItem = pricedItems.find((p: any) => p.formula_id === item.formula?.id);
          if (pricedItem) {
            return {
              ...item,
              calculatedPrice: {
                item_total: pricedItem.item_total,
                price_per_lb: pricedItem.price_per_lb,
                price_per_package: pricedItem.price_per_package,
                pricing_display_method: pricedItem.pricing_display_method,
                unit_price: pricedItem.unit_price,
              },
            };
          }
          return { ...item, calculatedPrice: undefined };
        })
      );
    } catch (err) {
      console.error('Error calculating pricing:', err);
    }
  }, [user, lineItems]);

  const addLineItem = () => {
    setLineItems((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        formula: null,
        selectedGrade: '',
        selectedAnimal: '',
        isPellet: false,
        packaging: 'bags',
        totalPounds: 0,
        numberOfPackages: 0,
        packageSizeLbs: 40,
      },
    ]);
  };

  // Handler for QuickAddProduct component
  const handleQuickAddItems = async (
    items: Array<{
      formula_id: number;
      name: string;
      sku: string | null;
      upc: string | null;
      is_pellet: boolean;
      bag_size: number;
      quantity: number;
      price_per_bag: number | null;
    }>,
    poMetadata?: { po_number?: string; po_date?: string }
  ) => {
    // Fetch all formulas once
    let allFormulas: FeedFormula[] = [];
    try {
      const response = await portalFeedsAPI.getAll();
      allFormulas = response.data;
    } catch (err) {
      console.error('Error fetching formulas:', err);
      return;
    }

    for (const item of items) {
      const formula = allFormulas.find((f: FeedFormula) => f.id === item.formula_id);

      if (formula) {
        // The feed's own bag; the scanned product's bag_size is the fallback.
        const packageSizeLbs = Number(formula.bag_size) > 0 ? Number(formula.bag_size) : (item.bag_size || 40);
        const numberOfPackages = item.quantity;
        const totalPounds = packageSizeLbs * item.quantity;

        setLineItems((prev) => {
          // Check if first item is empty
          const firstItem = prev[0];
          const isFirstEmpty = !firstItem.formula && firstItem.totalPounds === 0;

          const newItem: OrderLineItem = {
            id: crypto.randomUUID(),
            formula: formula,
            selectedGrade: formula.feed_grade,
            selectedAnimal: formula.animal_type,
            isPellet: item.is_pellet,
            packaging: 'bags',
            totalPounds,
            numberOfPackages,
            packageSizeLbs,
          };

          if (isFirstEmpty) {
            return [newItem];
          }
          return [...prev, newItem];
        });
      }
    }

    // Auto-fill PO number if present and field is empty
    if (poMetadata?.po_number && !customerPONumber) {
      setCustomerPONumber(poMetadata.po_number);
    }
  };

  const removeLineItem = (lineItemId: string) => {
    if (lineItems.length === 1) {
      alert('You must have at least one line item');
      return;
    }
    setLineItems((prev) => prev.filter((item) => item.id !== lineItemId));
  };

  const calculateOrderTotals = () => {
    let totalWeight = 0;
    let subtotal = 0;

    lineItems.forEach((item) => {
      if (!item.formula || item.totalPounds <= 0) return;
      totalWeight += item.totalPounds;
      if (item.calculatedPrice) {
        subtotal += item.calculatedPrice.item_total;
      }
    });

    const estimatedPallets = totalWeight > 0 ? Math.ceil(totalWeight / 2600) : 0;
    const isTaxExempt = user?.customer.is_tax_exempt || false;
    const taxRate = 0.08; // 8.0% Stevens County, WA
    const taxAmount = isTaxExempt ? 0 : subtotal * taxRate;
    const total = subtotal + taxAmount;

    return {
      totalWeight,
      subtotal,
      taxRate: taxRate * 100,
      taxAmount,
      total,
      estimatedPallets,
      isTaxExempt,
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!deliveryDate) {
      setError('Delivery date is required');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Validate pickup terminal for Terminal Pickup
    if (shippingMethod === 'Terminal Pickup' && !pickupTerminal) {
      setError('Please select a pickup terminal for Will Call orders');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Liftgate is a required explicit choice for Ship to Store — never leave it unset.
    if (shippingMethod === 'Ship to Store' && liftgateRequired === null) {
      setError('Please choose whether a liftgate is needed for this delivery');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const validLineItems = lineItems.filter((item) => item.formula && item.totalPounds > 0);
    if (validLineItems.length === 0) {
      setError('Please add at least one valid feed line item');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    try {
      setSubmitting(true);

      const orderData = {
        delivery_date: deliveryDate,
        shipping_method: shippingMethod,
        // Coerce to a real boolean: for Ship to Store the submit guard has already
        // forced an explicit choice; other methods don't take liftgate, so send false.
        liftgate_required: liftgateRequired === true,
        pickup_terminal: pickupTerminal,
        is_rush: isRush,
        special_instructions: specialInstructions,
        customer_po_number: customerPONumber,
        items: validLineItems.map((item) => ({
          formula: item.formula!.id,
          is_pellet: item.isPellet,
          package_type: apiPackageType(item),
          total_quantity_lbs: item.totalPounds,
          number_of_packages: item.numberOfPackages,
          unit_price_per_lb: item.calculatedPrice?.price_per_lb || 0,
          price_per_package: item.calculatedPrice?.price_per_package,
          pricing_display_method: item.calculatedPrice?.pricing_display_method || 'PER_LB',
        })),
      };

      const response = await portalOrdersAPI.create(orderData);
      const orderNumber = response.data?.order_number || '';
      const email = user?.email || '';
      setSuccessMessage(
        `Order #${orderNumber} placed successfully!${email ? ` Confirmation email sent to ${email}.` : ''}`
      );
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setTimeout(() => {
        navigate('/portal/orders');
      }, 3000);
    } catch (err: any) {
      const errData = err.response?.data;
      if (errData?.delivery_errors) {
        setError(errData.delivery_errors.join(' '));
        setDeliveryDateWarning(errData.delivery_errors[0]);
      } else {
        // Sanitize error: hide raw tracebacks from user
        let msg = errData?.error || 'Failed to place order. Please try again or contact us at (509) 738-9004.';
        if (typeof msg === 'string' && (msg.includes('Traceback') || msg.includes('File "/'))) {
          msg = 'Something went wrong placing your order. Please try again or call (509) 738-9004.';
        }
        setError(msg);
      }
      console.error(err);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setSubmitting(false);
    }
  };

  if (!user?.permissions.can_place_orders) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>
          You do not have permission to place orders. Please contact your account representative.
        </div>
        <PortalFooter />
      </div>
    );
  }

  const totals = calculateOrderTotals();

  return (
    <div style={styles.container}>
      <TutorialAutoStart pageKey="place-order" />

      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Place New Order</h1>
          <p style={styles.subtitle}>{user.customer.name}</p>
        </div>
        <TutorialHelpButton pageKey="place-order" />
      </div>

      {error && (
        <div key={error} style={styles.errorBox} className="portal-error-shake">
          {error}
        </div>
      )}

      {successMessage && (
        <div style={{
          background: '#d4edda', border: '1px solid #c3e6cb', color: '#155724',
          padding: '16px 20px', borderRadius: 8, marginBottom: 16, fontSize: 15,
        }}>
          <strong>{successMessage}</strong>
          <div style={{ marginTop: 6, fontSize: 13, color: '#1e7e34' }}>
            Redirecting to your orders...
          </div>
        </div>
      )}

      {/* Quick Add for Retail Partners */}
      {user.customer.customer_type === 'RETAIL_PARTNER' && (
        <div data-tutorial="quick-add-section">
          <QuickAddProduct onAddItems={handleQuickAddItems} />
        </div>
      )}

      <form onSubmit={handleSubmit} onKeyDown={(e) => {
        if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') {
          e.preventDefault();
        }
      }}>
        {/* Order Information */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Order Information</h2>

          {/* Delivery warning banner removed - inline delivery helper below shows correct dates with production lead time */}

          <div style={styles.formGrid}>
            {/* SHIPPING METHOD - FIRST (determines date helper) */}
            <div style={styles.formGroup} data-tutorial="shipping-method">
              <label htmlFor="shippingMethod" style={styles.label}>
                Shipping Method <span style={styles.required}>*</span>
              </label>
              <select
                id="shippingMethod"
                value={shippingMethod}
                onChange={(e) => {
                  setShippingMethod(e.target.value);
                  // Reset liftgate when switching away from Ship to Store
                  if (e.target.value !== 'Ship to Store') {
                    // Back to unset — returning to Ship to Store requires a fresh choice.
                    setLiftgateRequired(null);
                  }
                  // Reset pickup terminal when switching away from Terminal Pickup
                  if (e.target.value !== 'Terminal Pickup') {
                    setPickupTerminal(null);
                  }
                  // Clear delivery date when switching to Will Call (they pick their own day)
                  if (e.target.value === 'Will Call - Kettle Falls') {
                    setDeliveryDate('');
                  }
                }}
                required
                style={styles.input}
              >
                {PORTAL_SHIPPING_METHODS.map(method => (
                  <option key={method} value={method}>{method}</option>
                ))}
              </select>
            </div>

            {/* Liftgate — REQUIRED explicit choice for Ship to Store (business without
                dock). No default: the customer must pick before the order submits. */}
            {shippingMethod === 'Ship to Store' && (
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Liftgate <span style={styles.required}>*</span>
                </label>
                <label style={styles.checkboxLabel}>
                  <input
                    type="radio"
                    name="liftgate"
                    checked={liftgateRequired === true}
                    onChange={() => setLiftgateRequired(true)}
                    style={styles.checkbox}
                  />
                  <span style={styles.checkboxText}>Liftgate needed (no loading dock)</span>
                </label>
                <label style={styles.checkboxLabel}>
                  <input
                    type="radio"
                    name="liftgate"
                    checked={liftgateRequired === false}
                    onChange={() => setLiftgateRequired(false)}
                    style={styles.checkbox}
                  />
                  <span style={styles.checkboxText}>No liftgate (I can unload from a truck)</span>
                </label>
                <div style={styles.helperText}>
                  Required — a liftgate lowers freight to the ground when there's no dock or forklift.
                </div>
              </div>
            )}

            {/* Terminal selector - only for Terminal Pickup */}
            {shippingMethod === 'Terminal Pickup' && (
              <div style={styles.formGroup}>
                <label htmlFor="pickupTerminal" style={styles.label}>
                  Pickup Terminal <span style={styles.required}>*</span>
                </label>
                <TerminalSelector
                  value={pickupTerminal}
                  onChange={setPickupTerminal}
                  required
                  style={styles.input}
                />
                <div style={styles.helperText}>
                  Select the terminal where you'll pick up your order
                </div>
              </div>
            )}

            {/* DELIVERY DATE - SECOND (helper depends on shipping method) */}
            <div style={styles.formGroup} data-tutorial="delivery-date">
              <label htmlFor="deliveryDate" style={styles.label}>
                Delivery Date <span style={styles.required}>*</span>
              </label>
              <input
                id="deliveryDate"
                type="date"
                value={deliveryDate}
                onChange={(e) => setDeliveryDate(e.target.value)}
                required
                style={styles.input}
                min={new Date().toISOString().split('T')[0]}
              />

              {/* LTL Delivery Schedule Helper - Full Timeline */}
              {isFreightMethod(shippingMethod) && deliverySchedule && (
                <div style={{ marginTop: '6px', fontSize: '13px', lineHeight: '1.6' }}>
                  {deliverySchedule.delivery_day ? (
                    <>
                      <div style={{ color: '#508590' }}>
                        {deliverySchedule.is_daily
                          ? 'Your area has daily delivery (Tue–Fri)'
                          : `Peninsula delivers to your area on ${deliverySchedule.delivery_days_display || deliverySchedule.delivery_day + 's'}`
                        }
                      </div>
                      {deliverySchedule.can_make_nearest ? (
                        <div style={{ color: '#2e7d32', marginTop: '4px' }}>
                          ✅ Next delivery: {formatDate(deliverySchedule.next_delivery_date)} — order by {formatDate(deliverySchedule.order_cutoff_date)}
                        </div>
                      ) : (
                        <div style={{ color: '#e65100', marginTop: '4px' }}>
                          ⚠️ Next delivery: {formatDate(deliverySchedule.next_delivery_date)} — order by {formatDate(deliverySchedule.order_cutoff_date)}
                        </div>
                      )}
                      {deliverySchedule.remote_fee && (
                        <div style={{ color: '#92400e', marginTop: '4px', fontSize: '13px' }}>
                          💰 Remote delivery fee: ${deliverySchedule.remote_fee.toFixed(0)}
                        </div>
                      )}
                    </>
                  ) : (
                    <div style={{ color: '#508590' }}>
                      Your delivery schedule hasn't been set up yet. Call (509) 738-9004.
                    </div>
                  )}
                </div>
              )}

              {/* LTL but no schedule loaded yet */}
              {isFreightMethod(shippingMethod) && !deliverySchedule && (
                <div style={{ marginTop: '6px', fontSize: '13px', color: '#508590' }}>
                  Loading delivery schedule...
                </div>
              )}

              {/* Wrong delivery day warning */}
              {deliveryDateWarning && (
                <div style={{ marginTop: '6px', fontSize: '13px', color: '#e65100' }}>
                  {deliveryDateWarning}
                  {validDeliveryDates.length > 0 ? (
                    <div style={{ marginTop: '6px' }}>
                      <span style={{ color: '#555', fontSize: '12px' }}>Available dates: </span>
                      {validDeliveryDates.slice(0, 4).map((d) => {
                        const dt = new Date(d + 'T12:00:00');
                        const label = dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                        return (
                          <button
                            key={d}
                            type="button"
                            onClick={() => { setDeliveryDate(d); setDeliveryDateWarning(''); }}
                            style={{
                              marginRight: '6px',
                              color: '#1565c0',
                              background: 'none',
                              border: '1px solid #1565c0',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              padding: '3px 8px',
                            }}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  ) : deliverySchedule?.next_delivery_date && (
                    <button
                      type="button"
                      onClick={() => { setDeliveryDate(deliverySchedule.next_delivery_date); setDeliveryDateWarning(''); }}
                      style={{
                        marginLeft: '8px',
                        color: '#1565c0',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        fontSize: '13px',
                        padding: 0,
                      }}
                    >
                      Use {deliverySchedule.delivery_day} {formatDate(deliverySchedule.next_delivery_date)}
                    </button>
                  )}
                </div>
              )}

              {/* Will Call helper */}
              {shippingMethod.toLowerCase().includes('will call') && !willCallWarning && (
                <div style={{ marginTop: '6px', fontSize: '13px', color: '#508590' }}>
                  Pickup available Tuesday-Friday, 2:00-4:45 PM
                </div>
              )}

              {/* Will Call closed day warning */}
              {willCallWarning && (
                <div style={{ marginTop: '6px', fontSize: '13px', color: '#e65100' }}>
                  {willCallWarning}
                </div>
              )}
            </div>

            <div style={styles.formGroup}>
              <label htmlFor="customerPO" style={styles.label}>
                Your PO Number
              </label>
              <input
                id="customerPO"
                type="text"
                value={customerPONumber}
                onChange={(e) => setCustomerPONumber(e.target.value)}
                placeholder="Enter your PO number"
                style={styles.input}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={isRush}
                  onChange={(e) => setIsRush(e.target.checked)}
                />
                <span>Rush Order</span>
              </label>
            </div>
          </div>

          <div style={styles.formGroup}>
            <label htmlFor="specialInstructions" style={styles.label}>
              Special Instructions
            </label>
            <textarea
              id="specialInstructions"
              value={specialInstructions}
              onChange={(e) => setSpecialInstructions(e.target.value)}
              placeholder="Enter any special instructions for this order"
              style={{...styles.input, minHeight: '80px'}}
            />
          </div>
        </div>

        {/* Feed Line Items */}
        <div style={styles.section} data-tutorial="line-items">
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>Feed Items</h2>
          </div>

          {lineItems.map((item, index) => (
            <LineItemForm
              key={item.id}
              lineItem={item}
              index={index}
              feedGrades={feedGrades}
              onGradeChange={handleGradeChange}
              onAnimalChange={handleAnimalChange}
              onFormulaChange={handleFormulaChange}
              onPelletToggle={handlePelletToggle}
              onPackagingChange={handlePackagingChange}
              onTotalPoundsChange={handleTotalPoundsChange}
              onTotalPoundsBlur={handleTotalPoundsBlur}
              onNumberOfPackagesChange={handleNumberOfPackagesChange}
              onNumberOfPackagesBlur={handleNumberOfPackagesBlur}
              onRemove={removeLineItem}
              canRemove={lineItems.length > 1}
              loadAnimalTypes={loadAnimalTypes}
              loadFormulas={loadFormulas}
            />
          ))}

          <button
            type="button"
            onClick={addLineItem}
            style={styles.addButton}
            data-tutorial="add-line-btn"
          >
            + Add Feed Type
          </button>
        </div>

        {/* Shipping Estimate Card */}
        {shippingEstimate && (
          <div style={styles.shippingEstimateCard}>
            <div style={styles.shippingEstimateHeader}>
              <span style={styles.shippingEstimateIcon}>🚚</span>
              <span style={styles.shippingEstimateTitle}>Estimated Shipping</span>
            </div>

            {shippingEstimate.is_will_call ? (
              <div style={styles.shippingEstimateAmount}>
                <span style={styles.shippingPrice}>$0.00</span>
                <span style={styles.shippingBreakdown}>Will Call — No shipping charge</span>
              </div>
            ) : (
              <>
                <div style={styles.shippingEstimateAmount}>
                  <span style={styles.shippingPrice}>
                    ${shippingEstimate.total.toFixed(2)}
                  </span>
                  <span style={styles.shippingBreakdown}>
                    {shippingEstimate.breakdown_text}
                  </span>
                </div>

                {/* Retail partner incentive messaging */}
                {shippingEstimate.is_retail_partner && shippingEstimate.incentive_message && (
                  <div style={
                    shippingEstimate.retail_discount > 0
                      ? styles.shippingCelebration
                      : styles.shippingIncentive
                  }>
                    {shippingEstimate.incentive_message}
                  </div>
                )}
              </>
            )}

            <div style={styles.shippingDisclaimer}>
              * Estimate. Final cost confirmed on invoice.
            </div>
          </div>
        )}

        {loadingShipping && lineItems.some(i => i.formula && i.totalPounds > 0) && (
          <div style={styles.shippingEstimateCard}>
            <div style={styles.shippingLoading}>Calculating shipping...</div>
          </div>
        )}

        {/* Order Summary */}
        <div style={styles.section} data-tutorial="order-summary">
          <h2 style={styles.sectionTitle}>Order Summary</h2>

          <div style={styles.summaryBox}>
            <div style={styles.summaryRow}>
              <span>Customer:</span>
              <span>{user.customer.name}</span>
            </div>
            <div style={styles.summaryRow}>
              <span>Shipping:</span>
              <span>{shippingMethod}</span>
            </div>
            {liftgateRequired && (
              <div style={styles.summaryRow}>
                <span>Liftgate:</span>
                <span>Yes</span>
              </div>
            )}
            {deliveryDate && (
              <div style={styles.summaryRow}>
                <span>Delivery Date:</span>
                <span>{new Date(deliveryDate).toLocaleDateString()}</span>
              </div>
            )}

            <div style={styles.separator}></div>

            <div style={styles.summaryRow}>
              <span>Total Weight:</span>
              <span>{totals.totalWeight.toLocaleString()} lbs</span>
            </div>
            <div style={styles.summaryRow}>
              <span>Estimated Pallets:</span>
              <span>{totals.estimatedPallets}</span>
            </div>

            <div style={styles.separator}></div>

            {palletDiscount && palletDiscount.savings > 0 && (
              <div style={{ ...styles.summaryRow, color: '#166534' }}>
                <span>Pallet savings ({palletDiscount.pallets} pallets, ${palletDiscount.per_bag.toFixed(2)} off each bag):</span>
                <span>-${palletDiscount.savings.toFixed(2)}</span>
              </div>
            )}
            <div style={styles.summaryRow}>
              <span>Subtotal:</span>
              <span>${totals.subtotal.toFixed(2)}</span>
            </div>
            {!totals.isTaxExempt && (
              <div style={styles.summaryRow}>
                <span>Tax ({totals.taxRate}%):</span>
                <span>${totals.taxAmount.toFixed(2)}</span>
              </div>
            )}
            {totals.isTaxExempt && (
              <div style={styles.summaryRow}>
                <span>Tax:</span>
                <span>Exempt</span>
              </div>
            )}
            <div style={{...styles.summaryRow, ...styles.summaryTotal}}>
              <span>TOTAL:</span>
              <span>${totals.total.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Payment Notice */}
        <div style={{
          background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8,
          padding: '14px 18px', marginBottom: 16, display: 'flex', alignItems: 'flex-start', gap: 10,
        }}>
          <span style={{ fontSize: 20, lineHeight: 1 }}>&#x1F4B3;</span>
          <span style={{ fontSize: 14, color: '#1e40af', lineHeight: 1.5 }}>
            <strong>No payment is taken when you place your order.</strong> Once confirmed, you'll receive an invoice
            in your Billing section where you can pay by card or ACH.
          </span>
        </div>

        {/* Action Buttons */}
        <div style={styles.actionButtons}>
          <button
            type="button"
            onClick={() => navigate('/portal/dashboard')}
            style={styles.cancelButton}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting || !!successMessage || lineItems.filter(i => i.formula && i.totalPounds > 0).length === 0}
            style={{
              ...styles.submitButton,
              ...((submitting || !!successMessage || lineItems.filter(i => i.formula && i.totalPounds > 0).length === 0) ? styles.buttonDisabled : {}),
            }}
            data-tutorial="submit-btn"
          >
            {submitting ? 'Placing Order...' : successMessage ? 'Order Placed' : 'Place Order'}
          </button>
        </div>
      </form>

      <PortalFooter />
    </div>
  );
};

// Line Item Component
interface LineItemFormProps {
  lineItem: OrderLineItem;
  index: number;
  feedGrades: Array<{ value: string; label: string }>;
  onGradeChange: (id: string, grade: string) => void;
  onAnimalChange: (id: string, animal: string) => void;
  onFormulaChange: (id: string, formula: FeedFormula | null) => void;
  onPelletToggle: (id: string, isPellet: boolean) => void;
  onPackagingChange: (id: string, packaging: Packaging) => void;
  onTotalPoundsChange: (id: string, totalPounds: number) => void;
  onTotalPoundsBlur: (id: string) => void;
  onNumberOfPackagesChange: (id: string, numberOfPackages: number) => void;
  onNumberOfPackagesBlur: (id: string) => void;
  onRemove: (id: string) => void;
  canRemove: boolean;
  loadAnimalTypes: (grade: string) => Promise<Array<{ value: string; label: string }>>;
  loadFormulas: (grade: string, animal: string) => Promise<FeedFormula[]>;
}

const LineItemForm: React.FC<LineItemFormProps> = ({
  lineItem,
  index,
  feedGrades,
  onGradeChange,
  onAnimalChange,
  onFormulaChange,
  onPelletToggle,
  onPackagingChange,
  onTotalPoundsChange,
  onTotalPoundsBlur,
  onNumberOfPackagesChange,
  onNumberOfPackagesBlur,
  onRemove,
  canRemove,
  loadAnimalTypes,
  loadFormulas,
}) => {
  const [animalTypes, setAnimalTypes] = useState<Array<{ value: string; label: string }>>([]);
  const [formulas, setFormulas] = useState<FeedFormula[]>([]);

  useEffect(() => {
    if (lineItem.selectedGrade) {
      loadAnimalTypes(lineItem.selectedGrade).then(setAnimalTypes);
    } else {
      setAnimalTypes([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItem.selectedGrade]);

  useEffect(() => {
    if (lineItem.selectedGrade && lineItem.selectedAnimal) {
      loadFormulas(lineItem.selectedGrade, lineItem.selectedAnimal).then(setFormulas);
    } else {
      setFormulas([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItem.selectedGrade, lineItem.selectedAnimal]);

  const availableForms = lineItem.formula?.available_forms || [];
  const isMashAvailable = availableForms.includes('MASH');
  const isPelletAvailable = availableForms.includes('PELLET');
  const bagSize = bagSizeOf(lineItem.formula);

  return (
    <div style={styles.lineItem}>
      <div style={styles.lineItemHeader}>
        <span style={styles.lineItemNumber}>Feed Type #{index + 1}</span>
        {canRemove && (
          <button
            type="button"
            onClick={() => onRemove(lineItem.id)}
            style={styles.removeButton}
          >
            Remove
          </button>
        )}
      </div>

      {/* Grade and Animal */}
      <div style={styles.formGrid}>
        <div style={styles.formGroup}>
          <label style={styles.label}>
            Feed Grade <span style={styles.required}>*</span>
          </label>
          <select
            value={lineItem.selectedGrade}
            onChange={(e) => onGradeChange(lineItem.id, e.target.value)}
            style={styles.input}
          >
            <option value="">Select Grade...</option>
            {feedGrades.map((grade) => (
              <option key={grade.value} value={grade.value}>
                {grade.label}
              </option>
            ))}
          </select>
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>
            Animal Type <span style={styles.required}>*</span>
          </label>
          <select
            value={lineItem.selectedAnimal}
            onChange={(e) => onAnimalChange(lineItem.id, e.target.value)}
            disabled={!lineItem.selectedGrade}
            style={styles.input}
          >
            <option value="">Select Animal...</option>
            {animalTypes.map((animal) => (
              <option key={animal.value} value={animal.value}>
                {animal.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Formula */}
      <div style={styles.formGroup}>
        <label style={styles.label}>
          Formula <span style={styles.required}>*</span>
        </label>
        <select
          value={lineItem.formula?.id || ''}
          onChange={(e) => {
            const formula = formulas.find(f => f.id === Number(e.target.value)) || null;
            onFormulaChange(lineItem.id, formula);
          }}
          disabled={!lineItem.selectedAnimal}
          style={styles.input}
        >
          <option value="">Select Formula...</option>
          {formulas.map((formula) => (
            <option key={formula.id} value={formula.id}>
              {formula.name}
            </option>
          ))}
        </select>
      </div>

      {/* Form (Mash/Pellet) */}
      {lineItem.formula && (
        <div style={styles.formGroup}>
          <label style={styles.label}>Form</label>
          <div style={styles.radioGroup}>
            <label style={styles.radioLabel}>
              <input
                type="radio"
                checked={!lineItem.isPellet}
                onChange={() => onPelletToggle(lineItem.id, false)}
                disabled={!isMashAvailable}
              />
              <span>Mash</span>
            </label>
            <label style={styles.radioLabel}>
              <input
                type="radio"
                checked={lineItem.isPellet}
                onChange={() => onPelletToggle(lineItem.id, true)}
                disabled={!isPelletAvailable}
              />
              <span>Pellet</span>
            </label>
          </div>
          {!isMashAvailable && <div style={styles.hint}>Mash not available for this formula</div>}
          {!isPelletAvailable && <div style={styles.hint}>Pellets not available for this formula</div>}
          {lineItem.packaging === 'bags' && isPelletAvailable && (
            <div style={styles.hint}>Pellet only for Essential bags</div>
          )}
        </div>
      )}

      {/* Bags or bulk - the one packaging question. The bag size is the feed's. */}
      <div style={styles.formGroup}>
        <label style={styles.label}>
          Packaging <span style={styles.required}>*</span>
        </label>
        <div style={styles.radioGroup}>
          <label style={styles.radioLabel}>
            <input
              type="radio"
              name={`packaging-${lineItem.id}`}
              checked={lineItem.packaging === 'bags'}
              onChange={() => onPackagingChange(lineItem.id, 'bags')}
            />
            <span>Bags{lineItem.formula ? ` (${bagWord(bagSize)}s)` : ''}</span>
          </label>
          <label style={styles.radioLabel}>
            <input
              type="radio"
              name={`packaging-${lineItem.id}`}
              checked={lineItem.packaging === 'bulk'}
              onChange={() => onPackagingChange(lineItem.id, 'bulk')}
            />
            <span>Bulk (super sacks / totes)</span>
          </label>
        </div>
        <div style={styles.hint}>
          {lineItem.packaging === 'bags'
            ? 'Priced per bag. Enter how many bags, or a weight and we round up to whole bags.'
            : `Priced per pound, ${BULK_MINIMUM_LBS.toLocaleString()} lb minimum. Enter the weight and how many super sacks to split it into.`}
        </div>
      </div>

      {lineItem.packaging === 'bags' ? (
        <div style={styles.formGrid}>
          <div style={styles.formGroup}>
            <label style={styles.label}>
              How many bags? <span style={styles.required}>*</span>
            </label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder="e.g. 65"
              value={lineItem.numberOfPackages || ''}
              onChange={(e) => {
                const value = e.target.value;
                // Allow empty or numeric values only - NO calculations while typing
                if (value === '' || /^\d+$/.test(value)) {
                  onNumberOfPackagesChange(lineItem.id, value === '' ? 0 : Number(value));
                }
              }}
              onBlur={() => onNumberOfPackagesBlur(lineItem.id)}
              onFocus={(e) => e.target.select()}
              style={styles.input}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Total Pounds</label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder="or enter a weight"
              value={lineItem.totalPounds || ''}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '' || /^\d+$/.test(value)) {
                  onTotalPoundsChange(lineItem.id, value === '' ? 0 : Number(value));
                }
              }}
              onBlur={() => onTotalPoundsBlur(lineItem.id)}
              onFocus={(e) => e.target.select()}
              style={styles.input}
            />
          </div>
        </div>
      ) : (
        <div style={styles.formGrid}>
          <div style={styles.formGroup}>
            <label style={styles.label}>
              Total Pounds <span style={styles.required}>*</span>
            </label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder="e.g. 2600"
              value={lineItem.totalPounds || ''}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '' || /^\d+$/.test(value)) {
                  onTotalPoundsChange(lineItem.id, value === '' ? 0 : Number(value));
                }
              }}
              onBlur={() => onTotalPoundsBlur(lineItem.id)}
              onFocus={(e) => e.target.select()}
              style={styles.input}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>
              How many super sacks? <span style={styles.required}>*</span>
            </label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder="1"
              value={lineItem.numberOfPackages || ''}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '' || /^\d+$/.test(value)) {
                  onNumberOfPackagesChange(lineItem.id, value === '' ? 1 : Math.max(1, Number(value)));
                }
              }}
              onBlur={() => onNumberOfPackagesBlur(lineItem.id)}
              onFocus={(e) => e.target.select()}
              style={styles.input}
            />
          </div>
        </div>
      )}

      {/* What the line is, in the mill's words - the number the order will carry. */}
      {lineItem.totalPounds > 0 && lineItem.numberOfPackages > 0 && (
        <div style={styles.calculatedValue}>
          {lineItem.packaging === 'bags'
            ? `${lineItem.numberOfPackages} × ${bagWord(bagSize)}${lineItem.numberOfPackages === 1 ? '' : 's'} = ${lineItem.totalPounds.toLocaleString()} lb`
            : `${lineItem.numberOfPackages} × ${lineItem.packageSizeLbs.toLocaleString()} lb super sack${lineItem.numberOfPackages === 1 ? '' : 's'} = ${lineItem.totalPounds.toLocaleString()} lb`}
        </div>
      )}
      {lineItem.packaging === 'bulk' && lineItem.totalPounds > 0 && lineItem.totalPounds < BULK_MINIMUM_LBS && (
        <div style={{ ...styles.hint, color: '#e65100' }}>
          Bulk is sold from {BULK_MINIMUM_LBS.toLocaleString()} lb. Under that, choose Bags.
        </div>
      )}
      {lineItem.packaging === 'bulk' && lineItem.packageSizeLbs > SUPER_SACK_MAX_LBS && (
        <div style={{ ...styles.hint, color: '#e65100' }}>
          A super sack holds up to {SUPER_SACK_MAX_LBS.toLocaleString()} lb — split this into{' '}
          {Math.ceil(lineItem.totalPounds / SUPER_SACK_MAX_LBS)} sacks.
        </div>
      )}

      {/* Pricing */}
      {lineItem.calculatedPrice && (
        <div style={styles.pricingBox}>
          <div style={styles.pricingRow}>
            <span>Price:</span>
            <span>
              {lineItem.calculatedPrice.pricing_display_method === 'PER_PACKAGE' && lineItem.calculatedPrice.price_per_package
                ? `$${lineItem.calculatedPrice.price_per_package.toFixed(2)}/bag × ${lineItem.numberOfPackages} bags = `
                : `$${lineItem.calculatedPrice.price_per_lb?.toFixed(4)}/lb × ${lineItem.totalPounds.toLocaleString()} lbs = `
              }
              <strong>${lineItem.calculatedPrice.item_total.toFixed(2)}</strong>
            </span>
          </div>
        </div>
      )}

    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '1rem',
    backgroundColor: '#FAF9F6',
    minHeight: '100vh',
  },
  header: {
    marginBottom: '1.5rem',
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#3F2021',
    margin: '0 0 0.5rem 0',
  },
  subtitle: {
    fontSize: '1rem',
    color: '#508590',
    margin: 0,
  },
  section: {
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    border: '2px solid #C3AD8B',
    boxShadow: '0 2px 4px rgba(63, 32, 33, 0.1)',
    padding: '1rem',
    marginBottom: '1rem',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
    flexWrap: 'wrap',
    gap: '0.5rem',
  },
  sectionTitle: {
    fontSize: '1.125rem',
    fontWeight: 'bold',
    color: '#3F2021',
    margin: 0,
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '1rem',
    marginBottom: '1rem',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  label: {
    fontSize: '0.875rem',
    fontWeight: '600',
    color: '#3F2021',
  },
  required: {
    color: '#E73C3E',
  },
  input: {
    padding: '0.625rem',
    border: '2px solid #C3AD8B',
    borderRadius: '4px',
    fontSize: '16px', // Prevents iOS auto-zoom
    width: '100%',
    boxSizing: 'border-box',
    backgroundColor: '#FFFFFF',
    minHeight: '44px', // Touch target
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '0.95rem',
    cursor: 'pointer',
    color: '#3F2021',
  },
  checkbox: {
    width: '1.25rem',
    height: '1.25rem',
    cursor: 'pointer',
  },
  checkboxText: {
    fontSize: '0.95rem',
    color: '#3F2021',
  },
  helperText: {
    fontSize: '0.85rem',
    color: '#508590',
    marginTop: '0.25rem',
    marginLeft: '1.75rem',
  },
  radioGroup: {
    display: 'flex',
    gap: '1rem',
  },
  radioLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '0.95rem',
    cursor: 'pointer',
    color: '#3F2021',
  },
  hint: {
    fontSize: '0.8rem',
    color: '#508590',
    fontStyle: 'italic',
  },
  calculatedValue: {
    padding: '0.625rem',
    backgroundColor: '#FAF9F6',
    border: '2px solid #C3AD8B',
    borderRadius: '4px',
    fontSize: '0.95rem',
    fontWeight: '500',
    color: '#3F2021',
  },
  lineItem: {
    border: '2px solid #C3AD8B',
    borderRadius: '8px',
    padding: '1rem',
    marginBottom: '1rem',
    backgroundColor: '#FFFFFF',
  },
  lineItemHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
    paddingBottom: '0.75rem',
    borderBottom: '2px solid #C3AD8B',
  },
  lineItemNumber: {
    fontSize: '1rem',
    fontWeight: '600',
    color: '#3F2021',
  },
  addButton: {
    backgroundColor: '#6C8062',
    color: '#FFFFFF',
    border: 'none',
    padding: '0.625rem 1rem',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: '500',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 4px rgba(108, 128, 98, 0.3)',
    minHeight: '44px',
    width: '100%',
  },
  removeButton: {
    backgroundColor: '#E73C3E',
    color: '#FFFFFF',
    border: 'none',
    padding: '0.625rem 0.75rem',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.875rem',
    transition: 'all 0.2s ease',
    minHeight: '44px',
  },
  pricingBox: {
    backgroundColor: '#F0F7F4',
    border: '1px solid #6C8062',
    borderRadius: '4px',
    padding: '0.75rem',
    marginTop: '1rem',
  },
  pricingRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '0.95rem',
    color: '#3F2021',
  },
  materialsAvailable: {
    backgroundColor: '#D1FAE5',
    color: '#065F46',
    padding: '0.5rem',
    borderRadius: '4px',
    fontSize: '0.875rem',
    marginTop: '0.75rem',
    fontWeight: '500',
  },
  materialsUnavailable: {
    backgroundColor: '#FEE2E2',
    color: '#991B1B',
    padding: '0.5rem',
    borderRadius: '4px',
    fontSize: '0.875rem',
    marginTop: '0.75rem',
    fontWeight: '500',
  },
  summaryBox: {
    backgroundColor: '#FFFFFF',
    border: '2px solid #C3AD8B',
    padding: '1rem',
    borderRadius: '4px',
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.5rem 0',
    fontSize: '0.875rem',
    color: '#3F2021',
    flexWrap: 'wrap',
    gap: '0.25rem',
  },
  separator: {
    borderTop: '1px solid #C3AD8B',
    margin: '0.5rem 0',
  },
  summaryTotal: {
    fontSize: '1.125rem',
    fontWeight: 'bold',
    borderTop: '2px solid #E73C3E',
    paddingTop: '0.75rem',
    marginTop: '0.5rem',
    color: '#E73C3E',
  },
  actionButtons: {
    display: 'flex',
    gap: '0.75rem',
    justifyContent: 'stretch',
    marginTop: '1.5rem',
    marginBottom: '1.5rem',
    flexDirection: 'column',
  },
  cancelButton: {
    backgroundColor: '#508590',
    color: '#FFFFFF',
    border: 'none',
    padding: '0.75rem 1.5rem',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: '500',
    transition: 'all 0.2s ease',
    minHeight: '44px',
    width: '100%',
  },
  submitButton: {
    backgroundColor: '#E73C3E',
    color: '#FFFFFF',
    border: 'none',
    padding: '0.75rem 1.5rem',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: '500',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 4px rgba(231, 60, 62, 0.3)',
    minHeight: '44px',
    width: '100%',
  },
  buttonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  errorBox: {
    backgroundColor: '#fee2e2',
    border: '1px solid #fca5a5',
    color: '#991b1b',
    padding: '1rem',
    borderRadius: '4px',
    marginBottom: '1.5rem',
  },
  error: {
    backgroundColor: '#fee2e2',
    border: '1px solid #fca5a5',
    color: '#991b1b',
    padding: '1rem',
    borderRadius: '4px',
    textAlign: 'center',
  },
  // Shipping Estimate Card Styles
  shippingEstimateCard: {
    backgroundColor: '#f0f7fa',
    border: '1px solid #d1e3e8',
    borderRadius: '8px',
    padding: '1rem',
    marginTop: '1rem',
  },
  shippingEstimateHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '0.75rem',
  },
  shippingEstimateIcon: {
    fontSize: '1.25rem',
  },
  shippingEstimateTitle: {
    fontSize: '0.95rem',
    fontWeight: '600',
    color: '#1a3a4a',
    margin: 0,
  },
  shippingEstimateAmount: {
    marginBottom: '0.5rem',
  },
  shippingPrice: {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: '#1a3a4a',
    margin: 0,
  },
  shippingBreakdown: {
    fontSize: '0.85rem',
    color: '#4a6572',
    margin: '0.25rem 0 0 0',
  },
  shippingCelebration: {
    backgroundColor: '#d4edda',
    border: '1px solid #c3e6cb',
    borderRadius: '6px',
    padding: '0.75rem',
    marginTop: '0.75rem',
  },
  shippingIncentive: {
    backgroundColor: '#fff3cd',
    border: '1px solid #ffeeba',
    borderRadius: '6px',
    padding: '0.75rem',
    marginTop: '0.75rem',
    fontSize: '0.85rem',
    color: '#856404',
    fontWeight: '500',
  },
  shippingDisclaimer: {
    fontSize: '0.75rem',
    color: '#6b7c85',
    margin: '0.75rem 0 0 0',
    fontStyle: 'italic',
  },
  shippingLoading: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    color: '#4a6572',
    fontSize: '0.9rem',
  },
};

export default PortalPlaceOrder;
